/**
 * Sustainability Metric Emitter
 *
 * Implements tracking for:
 * - Clicks: Badge, Certification, CPF Logo, Expander, Secondary View
 * - Latency: Badge & Progressive Disclosure rendering
 * - Errors: Element rendering failures
 * - Lifecycle: Load and Render counts
 *
 * Now using @amzn/exs-client-logging for improved metrics handling
 */

import { CLICK_TYPE_TO_FEATURE_MAP } from "../../constants";
import { metricsLogger } from "./metric-logger";
import { logError } from "./logger";
import {
  EmitMetricOptions,
  StandardMetricTypes,
  type ClickType,
  type FeatureType,
} from "./metric-types";

export class SustainabilityMetricEmitter {
  private startTime: number;

  constructor() {
    this.startTime = performance.now();
  }

  resetStartTime(): void {
    this.startTime = performance.now();
  }

  /**
   * Track elapsed time and emit latency metric
   */
  private timeLog(metricName: string, options?: EmitMetricOptions): number {
    const { startTime } = this;
    const timeEnd = performance.now();
    const latency = timeEnd - startTime;

    this.latency(metricName, { value: latency, ...options });

    return latency;
  }

  /**
   * Convert dimension values to strings as required by CloudWatch API
   */
  private convertDimensionsToStrings(
    dimensions?: Record<string, string | number>,
  ): Record<string, string> | undefined {
    return dimensions
      ? Object.fromEntries(
          Object.entries(dimensions).map(([key, val]) => [key, String(val)]),
        )
      : undefined;
  }

  /**
   * Emit count metric
   */
  private count(metricName: string, options?: EmitMetricOptions): void {
    const { value = 1, dimensions } = options || {};
    this.emitCountMetric(metricName, value, dimensions);
  }

  /**
   * Emit latency metric
   */
  private latency(metricName: string, options?: EmitMetricOptions): void {
    const { value = 1, dimensions } = options || {};
    this.emitLatencyMetric(metricName, value, dimensions);
  }

  private emitCountMetric(
    metricName: string,
    value: number,
    dimensions?: Record<string, string | number>,
  ): void {
    try {
      if (!metricsLogger) {
        return;
      }

      const stringDimensions = this.convertDimensionsToStrings(dimensions);

      // Use exs-client-logging API: setDimensions, putMetric, flush
      if (stringDimensions) {
        metricsLogger.setDimensions(stringDimensions);
      }

      metricsLogger.putMetric(metricName, value);

      metricsLogger.flush();
    } catch (error) {
      logError({
        message: "Failed to emit count metric",
        error,
        shouldLogToConsole: true,
      });
    }
  }

  private emitLatencyMetric(
    metricName: string,
    value: number,
    dimensions?: Record<string, string | number>,
  ): void {
    try {
      if (!metricsLogger) {
        return;
      }

      const stringDimensions = this.convertDimensionsToStrings(dimensions);

      // Use exs-client-logging API: setDimensions, putMetric, flush
      if (stringDimensions) {
        metricsLogger.setDimensions(stringDimensions);
      }

      metricsLogger.putMetric(metricName, value, "Milliseconds");

      metricsLogger.flush();
    } catch (error) {
      logError({
        message: "Failed to emit latency metric",
        error,
        shouldLogToConsole: true,
      });
    }
  }

  trackClick(clickType: ClickType): void {
    this.count(StandardMetricTypes.InteractionCount, {
      dimensions: {
        type: `${clickType}-click`,
        featureType: CLICK_TYPE_TO_FEATURE_MAP[clickType],
      },
    });
  }

  trackLatency(featureType: FeatureType): number {
    return this.timeLog(StandardMetricTypes.RenderLatency, {
      dimensions: {
        featureType: `sustainability-features-element-${featureType}`,
      },
    });
  }

  trackError(reasonCode: string): void {
    this.count(StandardMetricTypes.RenderError, {
      dimensions: {
        reasonCode,
      },
    });
  }

  trackLoad(): void {
    this.count(StandardMetricTypes.LoadCount);
  }

  trackRender(): void {
    this.count(StandardMetricTypes.RenderCount);
  }
}
